import { getGoalDocById, updateGoal } from 'shared/services/backend-api';
import { Task } from 'shared/types/task';

export const updateLinkedGoal = async (
  taskPairs: Array<{ oldTask: Task; newTask: Task }>,
) => {
  if (!taskPairs.length) {
    return;
  }

  // Group task pairs by goalId, only include main tasks (no parentIds)
  const goalGroups = new Map<string, Array<{ oldTask: Task; newTask: Task }>>();

  taskPairs.forEach(({ oldTask, newTask }) => {
    if (!oldTask.goalId || oldTask.parentIds?.length) {
      return;
    }

    const goalId = oldTask.goalId;
    const existing = goalGroups.get(goalId) ?? [];
    goalGroups.set(goalId, [...existing, { oldTask, newTask }]);
  });

  // Update each goal once with all relevant tasks
  await Promise.all(
    Array.from(goalGroups.entries()).map(async ([goalId, pairs]) => {
      const goal = await getGoalDocById(goalId);
      if (!goal || (!goal.taskSorting?.length && !goal.sections?.length)) {
        return;
      }

      // Calculate counts from all main tasks
      const taskCount = pairs.length;
      const completedTaskCount = pairs.filter(
        ({ oldTask }) => !!oldTask.completedAt,
      ).length;

      const goalUpdateBase = {
        id: goal.id,
        taskCount,
        completedTaskCount,
      };

      // Handle taskSorting updates
      if (goal.taskSorting?.length) {
        const taskSorting = [...goal.taskSorting];
        // Sort pairs by their position in taskSorting (descending) to insert from end
        // This ensures indices remain correct when inserting multiple tasks
        const sortedPairs = pairs
          .map((pair) => ({
            ...pair,
            index: taskSorting.findIndex((id) => id === pair.oldTask.id),
          }))
          .filter((pair) => pair.index >= 0)
          .sort((a, b) => b.index - a.index);

        sortedPairs.forEach(({ newTask, index }) => {
          taskSorting.splice(index, 0, newTask.id);
        });

        updateGoal({ ...goalUpdateBase, taskSorting });
        return;
      }

      // Handle section updates
      if (goal.sections?.length) {
        const sections = goal.sections.map((section) => {
          const newSection = { ...section };
          const sectionTasks = [...(section.tasks ?? [])];
          let hasChanges = false;

          // Sort pairs by their position in section tasks (descending) to insert from end
          const sortedPairs = pairs
            .map((pair) => ({
              ...pair,
              index: sectionTasks.findIndex((id) => id === pair.oldTask.id),
            }))
            .filter((pair) => pair.index >= 0)
            .sort((a, b) => b.index - a.index);

          sortedPairs.forEach(({ newTask, index }) => {
            sectionTasks.splice(index, 0, newTask.id);
            hasChanges = true;
          });

          if (hasChanges) {
            newSection.tasks = sectionTasks;
          }

          return newSection;
        });

        updateGoal({ ...goalUpdateBase, sections });
      }
    }),
  );
};
